import ChargeHelper from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogAddChargeIdentityPartnerView from '../DialogAddChargeIdentityPartnerView';

ChargeIdentityPartnerListView.propTypes = {
  showFullUI: PropTypes.bool,
};

function ChargeIdentityPartnerListView({ showFullUI = false }) {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [filter, setFilter] = useState(ChargeHelper.Filter.chargeIdentityPartnerList);
  const [partnerList, setPartnerList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    ChargeHelper.API.requestGetChargeIdentityPartnerList(filter)
      .then((response) => {
        setPartnerList(response.partners ?? []);
        setTotal(response.total ?? 0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const openIdentifierListForPartner = useCallback((row) => {
    const id = row?.vehicleIdentityPartnerId;
    if (!id) return;
    Utils.openInNewTab(
      `${AppRoute.identifierList}?vehicleIdentityPartnerId=${encodeURIComponent(String(id))}`
    );
  }, []);

  const columns = useMemo(() => {
    const cols = [
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Logo
          </span>
        ),
        selector: (row) =>
          UIHelper.buildAvatar(Utils.getFullUrl(row?.logo), 40, null, false, false),
        width: '70px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Tên đối tác sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip text={row?.name}>
            <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
              {row?.name}
            </div>
          </KTTooltip>
        ),
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Website
          </span>
        ),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => { Utils.openInNewTab(row?.website); }}
          >
            <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
              {row?.website}
            </div>
          </span>
        ),
        minWidth: '220px',
        style: tableStyle.columns.link,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Địa chỉ
          </span>
        ),
        selector: (row) => (
          <KTTooltip text={row?.address}>
            <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
              {row?.address}
            </div>
          </KTTooltip>
        ),
        minWidth: '220px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Số mã định danh
          </span>
        ),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            style={UIHelper.buildFontStyle('600', '13px', AppColors.primary)}
            onClick={() => openIdentifierListForPartner(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openIdentifierListForPartner(row);
              }
            }}
          >
            {Utils.formatNumber(row?.vehicleIdentityCount ?? 0)}
          </span>
        ),
        width: '140px',
        right: true,
        style: tableStyle.columns.link,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mô tả
          </span>
        ),
        selector: (row) => (
          <KTTooltip text={row?.description}>
            <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
              {row?.description}
            </div>
          </KTTooltip>
        ),
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
    ];

    if (isAdmin) {
      cols.push({
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Thao tác
          </span>
        ),
        width: '100px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => UIHelper.buildMoreActionsButton(buildMoreActions(row), true),
      });
    }

    return cols;
  }, [isAdmin, openIdentifierListForPartner]);

  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.chargeIdentityPartnerList });
  };

  const buildMoreActions = (row) => {
    if (row?.isDeletable === false) {
      return [];
    }
    const iconStyle = { fontSize: '1.1rem' };
    return [
      {
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={iconStyle} />,
        click: () => handleAddNew(row),
      },
      {
        text: 'Xóa',
        iconElement: <i className="fa-regular fa-trash-can" style={iconStyle} />,
        click: () => ChargeHelper.UI.showDialogConfirmDeleteChargeIdentityPartner(row, handleRefresh),
      },
    ];
  };

  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Sửa đối tác sạc' : 'Thêm đối tác sạc',
      <DialogAddChargeIdentityPartnerView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      true,
      true
    );
  };

  return (
    <div className="px-4 my-4">
      {UIHelper.buildCard(
        <div>
          {showFullUI && (
            <div className="px-4 pt-4 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column align-items-start">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách đối tác sạc
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={{ color: AppColors.primary }}>
                    {Utils.formatNumber(total)} đối tác
                  </span>
                </span>
              </div>
              {isAdmin &&
                UIHelper.buildGreenButton(
                  'Thêm đối tác sạc',
                  () => handleAddNew(null),
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          )}
          <div className={`${showFullUI ? 'p-4' : 'pt-4'}`}>
            <div className="row m-0 mb-3">
              <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Tên đối tác, địa chỉ..."
                  value={ChargeHelper.Filter.chargeIdentityPartnerList.q}
                  onSubmit={(text) => {
                    ChargeHelper.Filter.chargeIdentityPartnerList = {
                      ...ChargeHelper.Filter.chargeIdentityPartnerList,
                      page: 0,
                      q: text,
                    };
                    handleRefresh();
                  }}
                />
              </div>
            </div>

            <div className="mb-3">
              <KTTable
                columns={columns}
                items={partnerList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={() => {}}
                onSelectedRowsChanged={() => {}}
                onDoubleClickedRow={() => {}}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>

            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={partnerList?.length}
              onChangePage={(newPage) => {
                ChargeHelper.Filter.chargeIdentityPartnerList = {
                  ...ChargeHelper.Filter.chargeIdentityPartnerList,
                  page: parseInt(newPage) - 1,
                };
                handleRefresh();
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                ChargeHelper.Filter.chargeIdentityPartnerList = {
                  ...ChargeHelper.Filter.chargeIdentityPartnerList,
                  limit: parseInt(newRowsPerPage),
                  page: 0,
                };
                handleRefresh();
              }}
              loading={isLoading}
              color={KTPaginationColor.success}
              appendText="đối tác"
            />
          </div>
        </div>,
        false,
        !showFullUI
      )}
    </div>
  );
}

export default ChargeIdentityPartnerListView;
